import React, { createContext, useContext, useState, useEffect } from 'react';
import { CONTACT_EMAILS, getBrandTitle } from '@/config/platform';
import { getLanguageFromHostname } from '@/config/domains';

type Language = 'pt' | 'en';

interface LanguageContextType {
  language: Language;
  setLanguage: (language: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

const translations = {
  pt: {
    // Navigation
    'nav.home': 'Início',
    'nav.about': 'Sobre Nós',
    'nav.blog': 'Blog',
    'nav.realEstate': 'Imobiliário',
    'nav.buyProperty': 'Comprar Casa',
    'nav.business': 'Negócios',
    'nav.expatGuides': 'Guias para Emigrantes',
    'nav.interactive': 'Kit de Relocalização',
    'nav.pathways': 'Pathways',
    'nav.destinations': 'Destinos',
    'nav.portugalVisas': 'Vistos Portugal',
    'nav.portugalServices': 'Serviços Portugal',
    'nav.toolkit': 'Toolkit',
    'nav.goldenVisa': 'Visto Gold',
    'nav.goldenVisaLink': '/visto-gold',
    'nav.partners': 'Parceiros',
    'nav.contact': 'Contato',
    'partners.destinations.other': 'Outros mercados',
    'partners.form.destinationsTitle': 'Em que destinos opera a sua empresa?',
    'partners.form.destinationsHint': 'Selecione todos os mercados onde presta serviços licenciados',
    'partners.form.otherMarkets': 'Especifique outros mercados',
    'partners.form.coveragePortugal': 'Cobertura em Portugal',
    'partners.form.coverageSpain': 'Cobertura em Espanha',
    'partners.form.coverageItaly': 'Cobertura em Itália',
    'partners.form.coverageGreece': 'Cobertura em Grécia',
    'healthcare': 'Saúde',
    
    // Common
    'common.readMore': 'Ler mais',
    'common.learnMore': 'Saiba mais',
    'common.getStarted': 'Começar',
    'common.contact': 'Contactar',
    'common.minutes': 'minutos',
    'common.readTime': 'min de leitura',
    
    // Hero page
    'hero.title': 'O Seu Caminho Inteligente para Residência Internacional',
    'hero.subtitle': 'Portugal, Espanha, Itália e Grécia — vistos, imóvel e ferramentas guiadas. Portugal como mercado flagship; Europa como horizonte.',
    'hero.cta': 'Explorar Destinos',
    'hero.ctaSecondary': 'Toolkit',
    
    // Expat Guides
    'guides.title': 'Guias Práticos para Emigrantes',
    'guides.intro': 'Mudar-se para Portugal pode ser uma experiência única, mas envolve vários detalhes a serem considerados. Neste espaço, criamos os Guias Práticos para Emigrantes, para ajudar você a entender como se estabelecer, gerenciar suas finanças, encontrar um lugar para morar e muito mais. Navegue por nossas seções e comece sua jornada de forma informada.',
    'guides.documentation.title': 'Documentação Necessária',
    'guides.documentation.description': 'Saiba tudo o que você precisa para obter o seu NIF, visto de residência e outros documentos essenciais para viver em Portugal.',
    'guides.finances.title': 'Finanças e Bancos',
    'guides.finances.description': 'Entenda como organizar suas finanças, abrir uma conta bancária em Portugal e como funciona o sistema fiscal.',
    'guides.housing.title': 'Habitação e Arrendamento',
    'guides.housing.description': 'Encontre o imóvel perfeito em Portugal! Saiba como alugar ou comprar sua casa no país.',
    'guides.health.title': 'Saúde e Seguro de Saúde',
    'guides.health.description': 'Descubra como funciona o sistema de saúde público em Portugal e as opções de seguro de saúde privado.',
    'guides.investment.title': 'Investir em Portugal',
    'guides.investment.description': 'Saiba mais sobre as oportunidades de investimento em Portugal, incluindo imóveis, fundos e o Golden Visa.',
    'guides.contact.description': 'Receba as melhores dicas sobre como viver e investir em Portugal diretamente no seu e-mail.',
    
    // Interactive Features
    'quiz.title': 'Qual é o Melhor Guia para Você?',
    'quiz.subtitle': 'Responda algumas perguntas e descubra o conteúdo mais relevante para seu perfil',
    'quiz.progress': 'Pergunta',
    'quiz.of': 'de',
    'quiz.completed': 'Quiz Concluído!',
    'quiz.recommendation': 'Baseado em suas respostas, recomendamos:',
    'quiz.startJourney': 'Iniciar Jornada',
    'quiz.restart': 'Refazer Quiz',
    'quiz.changeDestination': 'Mudar destino',
    'quiz.loading': 'A carregar pathways…',
    'quiz.noRulesTitle': 'Pathways em breve para este destino',
    'quiz.noRulesDescription': 'Ainda não temos regras de visto publicadas para este país. Registe interesse e a nossa equipa contacta quando estiver disponível.',
    'quiz.contactCta': 'Falar com especialista',
    'quiz.questions.status': 'Qual é sua situação atual?',
    'quiz.questions.interest': 'Qual é seu maior interesse em Portugal?',
    'quiz.questions.interestDestination': 'Qual é o seu maior interesse em {destination}?',
    'quiz.questions.region': 'Que região de Portugal mais te interessa?',
    'quiz.options.retired': 'Aposentado(a)',
    'quiz.options.working': 'Trabalhando',
    'quiz.options.investor': 'Investidor(a)',
    'quiz.options.nomad': 'Nômade Digital',
    'quiz.options.realEstate': 'Investimento Imobiliário',
    'quiz.options.lifestyle': 'Estilo de Vida',
    'quiz.options.business': 'Negócios',
    'quiz.options.retirementLife': 'Aposentadoria',
    'quiz.options.lisbon': 'Lisboa',
    'quiz.options.porto': 'Porto',
    'quiz.options.algarve': 'Algarve',
    'quiz.options.anywhere': 'Qualquer lugar',
    'quiz.results.retirement.title': 'Guia de Aposentadoria',
    'quiz.results.retirement.description': 'Perfeito para quem busca qualidade de vida e tranquilidade em Portugal',
    'quiz.results.investment.title': 'Guia de Investimento',
    'quiz.results.investment.description': 'Ideal para investidores interessados no mercado imobiliário português',
    'quiz.results.nomad.title': 'Guia para Nômades Digitais',
    'quiz.results.nomad.description': 'Especial para profissionais remotos que querem viver em Portugal',
    'quiz.results.business.title': 'Guia de Negócios',
    'quiz.results.business.description': 'Para empreendedores que querem estabelecer negócios em Portugal',

    'calculator.title': 'Calculadora de Custo de Vida',
    'calculator.subtitle': 'Descubra quanto custa viver em Portugal de acordo com seu perfil',
    'calculator.cities.lisbon': 'Lisboa',
    'calculator.cities.porto': 'Porto',
    'calculator.cities.algarve': 'Algarve',
    'calculator.cities.braga': 'Braga',
    'calculator.cities.coimbra': 'Coimbra',
    'calculator.categories.housing': 'Habitação',
    'calculator.categories.food': 'Alimentação',
    'calculator.categories.transport': 'Transporte',
    'calculator.categories.healthcare': 'Saúde',
    'calculator.categories.entertainment': 'Entretenimento',
    'calculator.categories.utilities': 'Utilidades',
    'calculator.categories.education': 'Educação',
    'calculator.categories.other': 'Outros',
    'calculator.form.title': 'Seus Dados',
    'calculator.form.subtitle': 'Preencha as informações para calcular',
    'calculator.form.income': 'Renda Mensal (€)',
    'calculator.form.city': 'Cidade de Interesse',
    'calculator.form.selectCity': 'Selecione uma cidade',
    'calculator.form.familySize': 'Tamanho da Família',
    'calculator.form.selectFamily': 'Selecione o número',
    'calculator.form.housingType': 'Tipo de Habitação',
    'calculator.form.selectHousing': 'Selecione o tipo',
    'calculator.form.lifestyle': 'Estilo de Vida',
    'calculator.form.selectLifestyle': 'Selecione seu estilo',
    'calculator.form.hasCar': 'Possui Automóvel',
    'calculator.form.selectCar': 'Selecione uma opção',
    'calculator.form.hasChildren': 'Tem Filhos em Idade Escolar',
    'calculator.form.selectChildren': 'Selecione uma opção',
    'calculator.form.person': 'pessoa',
    'calculator.form.people': 'pessoas',
    'calculator.form.calculate': 'Calcular',
    'calculator.form.reset': 'Limpar',
    'calculator.housing.apartment1br': 'Apartamento T1',
    'calculator.housing.apartment2br': 'Apartamento T2',
    'calculator.housing.apartment3br': 'Apartamento T3+',
    'calculator.housing.house': 'Casa/Moradia',
    'calculator.housing.luxury': 'Imóvel de Luxo',
    'calculator.lifestyle.basic': 'Básico',
    'calculator.lifestyle.moderate': 'Moderado',
    'calculator.lifestyle.comfortable': 'Confortável',
    'calculator.lifestyle.luxury': 'Luxuoso',
    'calculator.results.title': 'Resultados',
    'calculator.results.subtitle': 'Custo estimado mensal em',
    'calculator.results.empty': 'Preencha o formulário para ver os resultados',
    'calculator.results.total': 'Total Mensal',
    'calculator.results.percentage': 'Percentual da Renda',
    'calculator.results.ofIncome': 'da sua renda',
    'calculator.results.fillForm': 'Preencha o formulário ao lado para calcular',
    'calculator.assumptions.title': 'Pressupostos do Cálculo',
    'calculator.assumptions.housing': 'Habitação baseada em preços médios de arrendamento na região selecionada',
    'calculator.assumptions.food': 'Alimentação considera compras em supermercados e algumas refeições fora',
    'calculator.assumptions.transport': 'Transporte inclui passes mensais ou custos de combustível e manutenção',
    'calculator.assumptions.healthcare': 'Saúde baseada em seguro privado básico e consultas ocasionais',
    'calculator.assumptions.entertainment': 'Entretenimento inclui atividades culturais, restaurantes e lazer',
    'calculator.assumptions.utilities': 'Utilidades incluem água, luz, gás, internet e telemóvel',
    'calculator.assumptions.education': 'Educação considera custos de escola privada ou atividades extracurriculares',
    'calculator.assumptions.other': 'Outros gastos incluem vestuário, cuidados pessoais e imprevistos',
    'calculator.assumptions.note': 'Nota: Estes valores são estimativas baseadas em dados de 2024 e podem variar conforme necessidades individuais.',

    'interactiveGuides.title': 'Guias Interativos por Perfil',
    'interactiveGuides.subtitle': 'Guias por perfil — Portugal com conteúdo completo; outros destinos em expansão',
    'interactiveGuides.learnMore': 'Saiba Mais',
    'interactiveGuides.retirement.title': 'Aposentados',
    'interactiveGuides.retirement.description': 'Guia para aposentadoria tranquila na Europa — com foco em Portugal (mercado flagship)',
    'interactiveGuides.retirement.feature1': 'Sistema de saúde de qualidade',
    'interactiveGuides.retirement.feature2': 'Custo de vida acessível',
    'interactiveGuides.retirement.feature3': 'Clima ameno o ano todo',
    'interactiveGuides.nomad.title': 'Nômades Digitais',
    'interactiveGuides.nomad.description': 'Trabalhar remotamente na Europa — vistos e lifestyle, com guias aprofundados para Portugal',
    'interactiveGuides.nomad.feature1': 'Internet de alta velocidade',
    'interactiveGuides.nomad.feature2': 'Espaços de coworking',
    'interactiveGuides.nomad.feature3': 'Comunidade internacional',
    'interactiveGuides.investor.title': 'Investidores',
    'interactiveGuides.investor.description': 'Oportunidades de investimento e residência — Golden Visa e imóvel em Portugal; pathways nos outros destinos',
    'interactiveGuides.investor.feature1': 'Golden Visa disponível',
    'interactiveGuides.investor.feature2': 'Mercado imobiliário estável',
    'interactiveGuides.investor.feature3': 'Retorno sobre investimento',
    'interactiveGuides.business.title': 'Empreendedores',
    'interactiveGuides.business.description': 'Estabelecer negócio na Europa — Portugal como mercado flagship; parceiros nos destinos PT/ES/IT/GR',
    'interactiveGuides.business.feature1': 'Facilidade para abrir empresa',
    'interactiveGuides.business.feature2': 'Incentivos fiscais',
    'interactiveGuides.business.feature3': 'Acesso ao mercado europeu',
    'interactiveGuides.family.title': 'Famílias',
    'interactiveGuides.family.description': 'Mudança em família para a Europa — reagrupamento e residência, com guias detalhados para Portugal',
    'interactiveGuides.family.feature1': 'Educação pública de qualidade',
    'interactiveGuides.family.feature2': 'Segurança e tranquilidade',
    'interactiveGuides.family.feature3': 'Atividades para toda a família',

    'interactive.page.title': 'Kit de Relocalização',
    'interactive.page.subtitle': 'Ferramentas orientadas por dados para mapear o seu visto, custo de vida e região ideal—antes de falar com um consultor.',

    'toolkit.eyebrow': 'Plataforma de Residência Internacional',
    'toolkit.title': 'Kit de Relocalização',
    'toolkit.subtitle': 'Ferramentas já disponíveis para mapear o seu perfil, custos e elegibilidade—com Portugal em profundidade e mais destinos em expansão.',
    'toolkit.cta.hub': 'Ver destinos e pathways',
    'toolkit.cta.all': 'Abrir todas as ferramentas',
    'toolkit.items.quiz.title': 'Quiz de pathway',
    'toolkit.items.quiz.description': 'Responda em 2 minutos e descubra o guia e o visto mais alinhados ao seu perfil.',
    'toolkit.items.quiz.cta': 'Iniciar quiz',
    'toolkit.items.calculator.title': 'Calculadora de custo de vida',
    'toolkit.items.calculator.description': 'Estime despesas mensais por cidade e tamanho de família.',
    'toolkit.items.calculator.cta': 'Calcular custos',
    'toolkit.items.map.title': 'Mapa de regiões',
    'toolkit.items.map.description': 'Compare regiões por perfil: aposentado, nómada, investidor ou família.',
    'toolkit.items.map.cta': 'Explorar mapa',
    'toolkit.items.eligibility.title': 'Elegibilidade Golden Visa',
    'toolkit.items.eligibility.description': 'Verifique requisitos iniciais antes de uma consulta privada.',
    'toolkit.items.eligibility.cta': 'Verificar elegibilidade',
    'toolkit.items.advisor.title': 'Intake com consultor',
    'toolkit.items.advisor.description': 'Qualificação guiada e encaminhamento a parceiros licenciados.',
    'toolkit.items.advisor.cta': 'Falar com especialista',
    'toolkit.related.allVisas': 'Ver todos os vistos Portugal',
    'toolkit.related.pathwaysHub': 'Comparar destinos internacionais',
    'toolkit.related.d7': 'Guia Visto D7 (rendimentos passivos)',
    'toolkit.related.d8': 'Guia Visto D8 (nómada digital)',
    'toolkit.related.goldenVisa': 'Guia Golden Visa Portugal',
    'toolkit.related.startup': 'Guia Startup Visa Portugal',

    'destinations.title': 'Destinos internacionais',
    'portugalSpotlight.badge': 'Mercado flagship',
    'portugalSpotlight.title': 'Destaque Portugal',
    'portugalSpotlight.subtitle': 'O nosso mercado com conteúdo completo, vistos detalhados e rede de parceiros licenciados.',
    'portugalSpotlight.golden.title': 'Visto Gold',
    'portugalSpotlight.golden.description': 'Residência por investimento qualificado com acesso à UE — fundos, capital e vias alternativas.',
    'portugalSpotlight.golden.cta': 'Guia Golden Visa',
    'portugalSpotlight.d6.title': 'Visto D6 — Reagrupamento Familiar',
    'portugalSpotlight.d6.description': 'Reúna cônjuge, filhos e ascendentes dependentes com familiares já residentes em Portugal.',
    'portugalSpotlight.d6.cta': 'Guia Visto D6',
    'portugalSpotlight.hubCta': 'Hub Portugal',
    'portugalSpotlight.allVisasCta': 'Todos os vistos PT',
    'portugalSpotlight.consultCta': 'Consulta gratuita',
    'destinations.subtitle': 'Portugal é o nosso mercado flagship com conteúdo e parceiros completos. A arquitectura da plataforma prepara a expansão na UE.',
    'destinations.status.active': 'Disponível',
    'destinations.status.overview': 'Overview de pathways',
    'destinations.status.comingSoon': 'Em breve',
    'destinations.explorePathways': 'Ver pathways',
    'destinations.registerInterest': 'Registar interesse',
    'destinations.portugal.name': 'Portugal',
    'destinations.portugal.tagline': 'Golden Visa, D7, D8, imóvel e saúde—porta de entrada à UE.',
    'destinations.spain.name': 'Espanha',
    'destinations.spain.tagline': 'Comparador PT vs ES, 4 vias de residência e registo de interesse com concierge.',
    'destinations.italy.name': 'Itália',
    'destinations.italy.tagline': 'Vistos e lifestyle—em desenvolvimento.',
    'destinations.greece.name': 'Grécia',
    'destinations.greece.tagline': 'Golden Visa e costa—em desenvolvimento.',

    'chatInvite.title': 'Precisa de ajuda?',
    'chatInvite.body': 'Precisa de ajuda com residência internacional na UE? Fale com o assistente DENVORA Mobility — escolhe o destino no chat.',
    'chatInvite.cta': 'Iniciar conversa',

    'diagnostic.step1.title': 'Qual é o seu destino de relocalização?',
    'diagnostic.step1.subtitle': 'Portugal tem pathways completos hoje. Pode seleccionar outros destinos UE para registo de interesse.',
    'diagnostic.step2.title': 'Em que áreas precisa de apoio especializado?',
    'diagnostic.step2.subtitlePortugal': 'Pathways completos disponíveis para Portugal.',
    'diagnostic.step2.subtitleOther': 'Interesse registado para {destination} — a nossa equipa contactará quando o destino estiver activo.',
    'diagnostic.step2.comingSoonNote': 'As perguntas detalhadas abaixo estão calibradas para Portugal. O seu interesse no destino seleccionado será registado e partilhado com a equipa.',

    'pathways.page.title': 'Destinos & Pathways de Residência',
    'pathways.page.subtitle': 'Compare mercados, use ferramentas de pathway e avance com concierge discreto. Portugal com guias completos; Espanha com comparador e overview de vias; Itália e Grécia em registo de interesse.',
    'pathways.page.cta.advisor': 'Consulta privada',
    'pathways.pillars.title': 'Pathways Portugal — guias de visto',
    'pathways.pillars.subtitle': 'Aprofunde cada rota de residência depois de usar o toolkit. Conteúdo completo, bilíngue e actualizado.',
    'pathways.pillars.cta': 'Ler guia',
    'pathways.pillars.goldenVisa.title': 'Golden Visa',
    'pathways.pillars.goldenVisa.description': 'Residência por investimento qualificado — requisitos 2026 e rotas elegíveis.',
    'pathways.pillars.d7.title': 'Visto D7',
    'pathways.pillars.d7.description': 'Para aposentados e rendimentos passivos — rendimento mínimo e documentação.',
    'pathways.pillars.d8.title': 'Visto D8',
    'pathways.pillars.d8.description': 'Nómadas digitais e trabalho remoto para entidades fora de Portugal.',
    'pathways.pillars.startup.title': 'Startup Visa',
    'pathways.pillars.startup.description': 'Empreendedores com projecto inovador e potencial de crescimento em Portugal.',
    'pathways.pillars.allVisas.title': 'Todos os vistos D',
    'pathways.pillars.allVisas.description': 'Hub completo D1–D8 com comparação e ligações a cada página dedicada.',
    'pathways.comparator.title': 'Comparar pathways: Portugal vs Espanha',
    'pathways.comparator.subtitle': 'Mesmo perfil, dois mercados — rendimento mínimo, prazos e rota de residência lado a lado. Dados orientativos da base Pathway Engine.',
    'pathways.comparator.loading': 'A carregar comparador…',
    'pathways.comparator.family.passiveIncome': 'Rendimento passivo',
    'pathways.comparator.family.digitalNomad': 'Nómada digital',
    'pathways.comparator.family.investor': 'Investidor',
    'pathways.comparator.family.entrepreneur': 'Empreendedor',
    'pathways.comparator.minIncome': 'Rendimento mín.',
    'pathways.comparator.processing': 'Processamento',
    'pathways.comparator.citizenship': 'Nacionalidade (anos)',
    'pathways.comparator.months': 'meses',
    'pathways.comparator.years': 'anos',
    'pathways.comparator.viewGuide': 'Ver guia',
    'pathways.comparator.registerInterest': 'Registar interesse',
    'pathways.comparator.noRuleYet': 'Regras em publicação para este destino — registe interesse.',
    'pathways.comparator.disclaimer': 'Informação orientativa. Requisitos oficiais mudam — confirme com advogado de imigração antes de decidir.',
    'pathways.spain.badge': 'Overview de pathways',
    'pathways.spain.title': 'Pathways de residência em Espanha',
    'pathways.spain.subtitle': 'Quatro rotas principais para perfis de rendimento passivo, remoto, investimento e empreendedorismo — alinhadas com o comparador Portugal vs Espanha.',
    'pathways.spain.intro': 'Ainda não temos guias de visto página-a-página como em Portugal. Esta secção resume requisitos orientativos da Pathway Engine e encaminha para quiz ou consulta privada com destino Espanha.',
    'pathways.spain.highlight1': 'Acesso Schengen e mercado ibérico com forte procura de relocalização',
    'pathways.spain.highlight2': 'Vias para reforma, teletrabalho, capital qualificado e projectos empresariais',
    'pathways.spain.highlight3': 'Compare lado a lado com Portugal no comparador acima',
    'pathways.spain.cardCta': 'Registar interesse',
    'pathways.spain.ctaQuiz': 'Quiz com destino Espanha',
    'pathways.spain.ctaContact': 'Consulta privada — Espanha',
    'pathways.spain.exploreCta': 'Ver pathways Espanha',
    'pathways.spain.loading': 'A carregar pathways Espanha…',
    'pathways.spain.disclaimer': 'Valores indicativos (rendimento mínimo, prazos, nacionalidade). Legislação espanhola evolui — validação jurídica obrigatória antes de candidatura.',
    'pathways.italy.badge': 'Overview de pathways',
    'pathways.italy.title': 'Pathways de residência em Itália',
    'pathways.italy.subtitle': 'Quatro rotas para rendimento passivo, teletrabalho, investimento qualificado e projectos inovadores.',
    'pathways.italy.intro': 'Ainda não temos guias de visto página-a-página como em Portugal. Esta secção resume requisitos orientativos da Pathway Engine e encaminha para quiz ou consulta privada com destino Itália.',
    'pathways.italy.highlight1': 'Acesso Schengen, qualidade de vida mediterrânica e hubs para nómadas digitais',
    'pathways.italy.highlight2': 'Residenza elettiva, visto nómada, investidor (desde €250k) e startup visa',
    'pathways.italy.highlight3': 'Registe interesse enquanto expandimos conteúdo dedicado à Itália',
    'pathways.italy.cardCta': 'Registar interesse',
    'pathways.italy.ctaQuiz': 'Quiz com destino Itália',
    'pathways.italy.ctaContact': 'Consulta privada — Itália',
    'pathways.italy.exploreCta': 'Ver pathways Itália',
    'pathways.italy.loading': 'A carregar pathways Itália…',
    'pathways.italy.disclaimer': 'Valores indicativos (rendimento mínimo, prazos, nacionalidade). Legislação italiana evolui — validação jurídica obrigatória antes de candidatura.',
    'pathways.greece.badge': 'Overview de pathways',
    'pathways.greece.title': 'Pathways de residência na Grécia',
    'pathways.greece.subtitle': 'Permissão financeiramente independente, nómada digital, Golden Visa imobiliário e residência empresarial.',
    'pathways.greece.intro': 'Ainda não temos guias de visto página-a-página como em Portugal. Esta secção resume requisitos orientativos da Pathway Engine e encaminha para quiz ou consulta privada com destino Grécia.',
    'pathways.greece.highlight1': 'Acesso Schengen, custo de vida competitivo e Golden Visa imobiliário conhecido',
    'pathways.greece.highlight2': 'Rotas para reforma, teletrabalho, investimento imobiliário e projectos empresariais',
    'pathways.greece.highlight3': 'Use o quiz com destino Grécia para alinhar perfil e pathway',
    'pathways.greece.cardCta': 'Registar interesse',
    'pathways.greece.ctaQuiz': 'Quiz com destino Grécia',
    'pathways.greece.ctaContact': 'Consulta privada — Grécia',
    'pathways.greece.exploreCta': 'Ver pathways Grécia',
    'pathways.greece.loading': 'A carregar pathways Grécia…',
    'pathways.greece.disclaimer': 'Valores indicativos (rendimento mínimo, prazos, nacionalidade). Legislação grega evolui — validação jurídica obrigatória antes de candidatura.',
    'pathways.faq.title': 'Perguntas frequentes — residência internacional',
    'pathways.faq.q1': 'O que é a plataforma de residência internacional?',
    'pathways.faq.a1': 'É a área da DENVORA Mobility que combina ferramentas guiadas (quiz, calculadora, mapa), conteúdo sobre vistos e apoio humano. Ajuda a escolher o país e a via de residência antes de falar com um consultor.',
    'pathways.faq.q2': 'Que destinos estão disponíveis hoje?',
    'pathways.faq.a2': 'Portugal é o mercado flagship com toolkit completo e guias de visto dedicados. Espanha, Itália e Grécia têm overview de pathways, quiz e registo de interesse — Espanha inclui comparador PT vs ES. Guias página-a-página fora de Portugal estão em desenvolvimento.',
    'pathways.faq.q3': 'Como funcionam as ferramentas do toolkit?',
    'pathways.faq.a3': 'O quiz de pathway funciona para Portugal, Espanha, Itália e Grécia. A calculadora e o mapa focam-se em Portugal; o comparador cobre Portugal vs Espanha. O intake com consultor qualifica o projecto e associa o destino seleccionado.',
    'pathways.faq.q4': 'Esta plataforma substitui aconselhamento jurídico?',
    'pathways.faq.a4': 'Não. As ferramentas e guias são informativos e de orientação. Decisões de visto, fiscalidade e investimento devem ser validadas com advogado de imigração e consultores licenciados.',
    'pathways.faq.q5': 'Como avançar para uma consulta privada?',
    'pathways.faq.a5': 'Use o botão «Consulta privada» ou o intake do toolkit. O destino seleccionado (por defeito Portugal) é associado ao seu pedido para encaminhamento correcto.',

    'interactiveMap.title': 'Mapa de Oportunidades por Região',
    'interactiveMap.description': 'Descubra as melhores regiões de Portugal para seu perfil e objetivos',
    'interactiveMap.legend.retirees': 'Aposentados',
    'interactiveMap.legend.nomads': 'Nômades Digitais',
    'interactiveMap.legend.investors': 'Investidores',
    'interactiveMap.costOfLiving': 'Custo de Vida',
    'interactiveMap.highlights': 'Destaques',
    'interactiveMap.clickInstructions': 'Clique nos marcadores para ver detalhes de cada região',

    // Footer
    'footer.description': 'Inteligência de residência internacional em Portugal, Espanha, Itália e Grécia. Vistos, pathways, imóvel e concierge discreto.',
    'footer.destinations': 'Destinos',
    'footer.company': 'Empresa',
    'footer.newsletter.title': 'Receba dicas exclusivas no seu e-mail',
    'footer.newsletter.description': 'Newsletter semanal com oportunidades, análises de mercado e guias práticos',
    'footer.newsletter.placeholder': 'Seu e-mail',
    'footer.newsletter.subscribe': 'Subscrever',
    'footer.quickLinks': 'Links Rápidos',
    'footer.services': 'Serviços',
    'footer.services.buyerAgent': 'Buyer Agent',
    'footer.services.goldenVisa': 'Visto Gold',
    'footer.services.d7Visa': 'Visto D7',
    'footer.services.investment': 'Investimento',
    'footer.services.legalConsultancy': 'Consultoria Legal',
    'footer.services.financing': 'Financiamento',
    'footer.services.d2Visa': 'Visto D2',
    'footer.legal': 'Legal',
    'footer.legal.privacy': 'Política de Privacidade',
    'footer.legal.terms': 'Termos de Uso',
    'footer.legal.gdpr': 'RGPD',
    'footer.legal.complaints': 'Livro de Reclamações',
    'footer.followUs': 'Siga-nos',
    'footer.rights': 'Todos os direitos reservados.',

    // Golden Visa Section
    'goldenVisa.badge': 'VISTO GOLD',
    'goldenVisa.title': 'Portugal Golden Visa',
    'goldenVisa.subtitle': 'Garanta residência em Portugal e livre circulação na Europa através de investimento estratégico. O caminho mais rápido para a cidadania europeia.',
    'goldenVisa.stats.countries': 'Países UE',
    'goldenVisa.stats.years': 'Anos para cidadania (7 na CPLP/UE)',
    'goldenVisa.stats.days': 'Dias/2 anos mínimo',
    'goldenVisa.stats.investment': 'Investimento mínimo',
    'goldenVisa.benefitsTitle': 'Principais Benefícios',
    'goldenVisa.benefits.euAccess.title': 'Acesso à UE',
    'goldenVisa.benefits.euAccess.description': 'Livre circulação em 27 países da União Europeia',
    'goldenVisa.benefits.family.title': 'Inclusão Familiar',
    'goldenVisa.benefits.family.description': 'Cônjuge, filhos e pais incluídos no processo',
    'goldenVisa.benefits.minimal.title': 'Permanência Mínima',
    'goldenVisa.benefits.minimal.description': 'Apenas 14 dias a cada 2 anos para manter residência',
    'goldenVisa.benefits.investment.title': 'Diversas Opções',
    'goldenVisa.benefits.investment.description': 'Múltiplas modalidades de investimento disponíveis',
    'goldenVisa.optionsTitle': 'Modalidades de Investimento',
    'goldenVisa.options.funds.title': 'Fundos de Investimento',
    'goldenVisa.options.funds.description': 'Investimento em fundos de capital de risco licenciados',
    'goldenVisa.options.funds.details': 'Fundos qualificados com foco em inovação, tecnologia ou apoio a PMEs portuguesas',
    'goldenVisa.options.business.title': 'Criação de Emprego',
    'goldenVisa.options.business.amount': 'Variável',
    'goldenVisa.options.business.description': 'Investimento com criação mínima de 5 postos de trabalho',
    'goldenVisa.options.business.details': 'Criação de pelo menos 5 postos de trabalho permanentes ou 4 em zonas de baixa densidade',
    'goldenVisa.options.culture.title': 'Artes & Cultura',
    'goldenVisa.options.culture.description': 'Investimento em património cultural e projetos artísticos',
    'goldenVisa.options.culture.details': 'Recuperação de património cultural, apoio à produção artística nacional ou reconstrução urbana',
    'goldenVisa.options.research.title': 'Investigação Científica',
    'goldenVisa.options.research.description': 'Investimento em atividades de investigação desenvolvidas por instituições',
    'goldenVisa.options.research.details': 'Públicas ou privadas, integradas no sistema científico e tecnológico nacional',
    'goldenVisa.options.capital.title': 'Transferência de Capital',
    'goldenVisa.options.capital.description': 'Transferência de capitais no montante igual ou superior a 1.500.000 euros',
    'goldenVisa.options.capital.details': 'Para conta bancária em Portugal ou aplicação em instrumentos financieiros portugueses',
    'goldenVisa.options.support.title': 'Apoio à Produção Nacional',
    'goldenVisa.options.support.description': 'Investimento na constituição de sociedade comercial com sede em território nacional',
    'goldenVisa.options.support.details': 'Criação de 5 postos de trabalho ou aumento de capital social para reforçar a atividade',
    'goldenVisa.popular': 'Mais Popular',
    'goldenVisa.cta.title': 'Pronto para o Próximo Passo?',
    'goldenVisa.cta.description': 'Descubra se é elegível para o Visto Gold e inicie a sua jornada para a residência europeia com a orientação dos nossos especialistas.',
    'goldenVisa.cta.learn': 'Saber mais sobre Visto Gold',
    'goldenVisa.cta.consult': 'Consulta Gratuita',

    // CTA Section
    'cta.title': 'Conecte-se com Especialistas de Confiança',
    'cta.subtitle': 'Aceda aos melhores serviços através da nossa rede de parceiros especializados. Cada um é cuidadosamente selecionado pela sua expertise e compromisso com a excelência.',
    'cta.services.real_estate.title': 'Imobiliário',
    'cta.services.real_estate.description': 'Encontre a casa perfeita com a Homesconsult - Buyer&apos;s Agent especializada em trabalhar para você',
    'cta.services.real_estate.cta': 'Contactar Homesconsult',
    'cta.services.finance.title': 'Finanças',
    'cta.services.finance.description': 'Optimize os seus investimentos e financiamentos com nossos parceiros financeiros de confiança',
    'cta.services.finance.cta': 'Saiba mais',
    'cta.services.business.title': 'Negócios',
    'cta.services.business.description': 'Abra ou expanda o seu negócio na Europa com suporte dos nossos parceiros — Portugal, Espanha, Itália e Grécia',
    'cta.services.business.cta': 'Saiba mais',
    'cta.partner_highlight': 'Parceiro Principal',
    'cta.partners_verified': 'Todos os nossos parceiros são cuidadosamente verificados e recomendados com base na sua experiência e resultados comprovados.',
    'cta.certified_experts': 'Especialistas Certificados',
    'cta.portuguese_support': 'Suporte em Português',
    'cta.proven_experience': 'Experiência Comprovada',

    // Educational Content
    'educational.title': 'Conteúdo Educativo',
    'educational.subtitle': 'Insights sobre residência e imóvel nos nossos destinos — Portugal em destaque; Espanha, Itália e Grécia em expansão',
    'educational.read_more': 'Ler mais',
    'educational.view_all': 'Ver Todos os Artigos',

    // Services Section
    'services.title': 'Parceiros de Confiança',
    'services.subtitle': 'Conectamos você com os melhores especialistas em cada área, cuidadosamente selecionados pela sua expertise e compromisso com a excelência',
    'services.realEstate.title': 'Imobiliário & Buyer Agent',
    'services.realEstate.description': 'Apoio à compra de imóvel com buyer agent — mercado flagship Portugal (Homesconsult); outros destinos via parceiros',
    'services.realEstate.details': 'Conectamos com a Homesconsult e especialistas buyers agent em Portugal. Para Espanha, Itália e Grécia, encaminhamos para parceiros licenciados nos respetivos mercados.',
    'services.realEstate.cta': 'Contactar Homesconsult',
    'services.financial.title': 'Serviços Financeiros & Visto Gold',
    'services.financial.description': 'Rede de parceiros para financiamento, Golden Visa e otimização fiscal',
    'services.financial.details': 'Consultores em Golden Visa e financiamento com cobertura em Portugal, Espanha, Itália e Grécia — maior profundidade em Portugal.',
    'services.financial.cta': 'Saiba mais',
    'services.business.title': 'Apoio aos Negócios',
    'services.business.description': 'Parceiros em constituição e gestão de empresas nos mercados PT, ES, IT e GR',
    'services.business.details': 'Desde abertura de empresa até contabilidade e consultoria legal — conectamos com profissionais nos destinos que selecionar.',
    'services.business.cta': 'Saiba mais',

    // Blog Section
    'blog.title': 'Blog',
    'blog.subtitle': 'Artigos sobre residência, vistos e oportunidades — Portugal em destaque; perspetiva multi-destino em crescimento',
    'blog.featured': 'Destaque',
    'blog.readMore': 'Ler artigo completo',
    'blog.viewAll': 'Ver Todos os Artigos do Blog',
    'blog.filter.all': 'Todos',
    'blog.filter.europe': 'Europa & Pathways',
    'blog.filter.portugal': 'Portugal',
    'blog.filter.spain': 'Espanha',
    'blog.filter.italy': 'Itália',
    'blog.filter.greece': 'Grécia',
    'blog.filter.topic.all': 'Todos os temas',
    'blog.filter.topic.pathways': 'Pathways',
    'blog.filter.topic.visas': 'Vistos & residência',
    'blog.filter.topic.property': 'Imobiliário',
    'blog.filter.topic.living': 'Viver',
    'blog.filter.topic.tax': 'Fiscalidade',
    'blog.filter.count': '{n} artigos',
    'blog.empty.title': 'Conteúdo em preparação',
    'blog.empty.spain': 'Estamos a preparar artigos sobre residência em Espanha. Explore os pathways ou registe interesse connosco.',
    'blog.empty.italy': 'Estamos a preparar artigos sobre residência em Itália. Explore os pathways ou registe interesse connosco.',
    'blog.empty.greece': 'Estamos a preparar artigos sobre residência na Grécia. Explore os pathways ou registe interesse connosco.',
    'blog.empty.ctaPathways': 'Ver Pathways',
    'blog.empty.ctaContact': 'Pedir consulta',
    'blog.hub.ctaDestination': 'Explorar hub do destino',
    'blog.hub.noTopicMatch': 'Nenhum artigo neste tema — escolha outro filtro ou veja todos os artigos do destino.',
    'blog.articles.residenceLicense.category': 'Residência',
    'blog.articles.residenceLicense.title': 'Licença de Residência em Portugal: O Guia Definitivo para Viver o Seu Sonho',
    'blog.articles.residenceLicense.excerpt': 'A decisão de se mudar para Portugal é o primeiro passo de uma jornada incrível. Entenda o processo legal para garantir a sua residência.',
    'blog.articles.lisbonMarket.category': 'Mercado Imobiliário',
    'blog.articles.lisbonMarket.title': 'Lisboa: O Mercado Imobiliário do Momento',
    'blog.articles.lisbonMarket.excerpt': 'Lisboa não é apenas uma capital histórica; é também um dos mercados imobiliários mais quentes da Europa.',
    'blog.articles.buyingProperty.category': 'Compra de Imóveis',
    'blog.articles.buyingProperty.title': 'O Guia Essencial: O Processo de Compra de Imóvel em Portugal',
    'blog.articles.buyingProperty.excerpt': 'A decisão de comprar uma casa em Portugal é um passo emocionante. Este guia mostra cada passo do percurso de forma clara.',
    'blog.articles.investmentPortugal.category': 'Investimento',
    'blog.articles.investmentPortugal.title': 'Investir em Portugal: Estratégias e Oportunidades para um Portefólio Sólido',
    'blog.articles.investmentPortugal.excerpt': 'Portugal emergiu como um farol para investidores imobiliários internacionais. Descubra estratégias para maximizar o retorno.',

    // Calculator Toast Messages
    'calculator.toast.required_fields.title': 'Campos obrigatórios',
    'calculator.toast.required_fields.description': 'Por favor, preencha todos os campos antes de calcular.',
    'calculator.toast.invalid_value.title': 'Valor inválido',
    'calculator.toast.invalid_value.description': 'Por favor, insira um valor de renda válido.',
    'calculator.toast.calculation_done.title': 'Cálculo realizado!',
    'calculator.toast.form_cleared.title': 'Formulário limpo',
    'calculator.toast.form_cleared.description': 'Todos os campos foram resetados.',

    // Golden Visa Page Specific Content
    'goldenVisaPage.title': 'Visto Gold Portugal - Residência através de Investimento',
    'goldenVisaPage.description': 'Obtenha o Visto Gold português através de investimento estratégico. Residência em Portugal, acesso à UE e caminho para cidadania europeia.',
    'goldenVisaPage.hero.badge': 'PROGRAMA OFICIAL',
    'goldenVisaPage.hero.title': 'Visto Gold Portugal',
    'goldenVisaPage.hero.subtitle': 'Obtenha residência portuguesa e acesso livre à União Europeia através de investimento qualificado. O caminho mais direto para a cidadania europeia.',
    'goldenVisaPage.hero.cta.primary': 'Verificar Elegibilidade',
    'goldenVisaPage.hero.cta.secondary': 'Descarregar Guia Completo',
    'goldenVisaPage.overview.title': 'Visão Geral do Programa',
    'goldenVisaPage.overview.description': 'O Visto Gold português é um programa de residência por investimento que oferece aos cidadãos de países terceiros a oportunidade de obter autorização de residência em Portugal através de investimento qualificado.',
    'goldenVisaPage.overview.features.title': 'Características Principais',
    'goldenVisaPage.overview.features.1': 'Autorização de residência válida por 2 anos, renovável por períodos de 3 anos',
    'goldenVisaPage.overview.features.2': 'Possibilidade de requerer cidadania portuguesa após 10 anos de residência legal, ou 7 anos para cidadãos da CPLP ou da UE. Pedidos apresentados até 18/05/2026 ainda podiam usar o prazo anterior de 5 anos.',
    'goldenVisaPage.overview.features.3': 'Livre circulação no Espaço Schengen',
    'goldenVisaPage.overview.features.4': 'Inclusão de cônjuge, filhos menores e pais dependentes',
    'goldenVisaPage.benefits.tag': 'VANTAGENS',
    'goldenVisaPage.benefits.title': 'Benefícios do Visto Gold',
    'goldenVisaPage.benefits.subtitle': 'Descubra todas as vantagens que o programa oferece para si e sua família',
    'goldenVisaPage.routes.tag': 'MODALIDADES',
    'goldenVisaPage.routes.title': 'Rotas de Investimento Disponíveis',
    'goldenVisaPage.routes.subtitle': 'Escolha a modalidade de investimento que melhor se adequa ao seu perfil',
    'goldenVisaPage.eligibility.tag': 'REQUISITOS',
    'goldenVisaPage.eligibility.title': 'Critérios de Elegibilidade',
    'goldenVisaPage.eligibility.subtitle': 'Confirme se cumpre os requisitos para solicitar o Visto Gold',
    'goldenVisaPage.eligibility.requirements.title': 'Requisitos Gerais',
    'goldenVisaPage.eligibility.requirements.1': 'Ser cidadão de país terceiro (fora da UE/EEE/Suíça)',
    'goldenVisaPage.eligibility.requirements.2': 'Não ter antecedentes criminais',
    'goldenVisaPage.eligibility.requirements.3': 'Dispor de meios de subsistência',
    'goldenVisaPage.eligibility.requirements.4': 'Ter seguro de saúde',
    'goldenVisaPage.eligibility.requirements.5': 'Cumprir uma das modalidades de investimento',
    'goldenVisaPage.presence.tag': 'PERMANÊNCIA',
    'goldenVisaPage.presence.title': 'Requisitos de Permanência Física',
    'goldenVisaPage.presence.subtitle': 'Tempo mínimo de permanência exigido para manter a residência',
    'goldenVisaPage.presence.minimum.title': 'Permanência Mínima',
    'goldenVisaPage.presence.minimum.description': 'Para manter o Visto Gold, é necessário permanecer em Portugal:',
    'goldenVisaPage.presence.minimum.first': '7 dias, seguidos ou interpolados, no primeiro ano',
    'goldenVisaPage.presence.minimum.subsequent': '14 dias, seguidos ou interpolados, em cada período subsequente de 2 anos',
    'goldenVisaPage.presence.minimum.total': 'Total: 35 dias ao longo dos primeiros 5 anos (7 + 14 + 14)',
    'goldenVisaPage.process.tag': 'PROCESSO',
    'goldenVisaPage.process.title': 'Cronograma do Processo',
    'goldenVisaPage.process.subtitle': 'Entenda cada etapa do processo de candidatura',
    'goldenVisaPage.documents.tag': 'DOCUMENTAÇÃO',
    'goldenVisaPage.documents.title': 'Documentos e Custos',
    'goldenVisaPage.documents.subtitle': 'Lista completa de documentos necessários e custos associados',
    'goldenVisaPage.documents.required.title': 'Documentos Obrigatórios',
    'goldenVisaPage.documents.costs.title': 'Custos do Processo',
    'goldenVisaPage.documents.costs.application': 'Taxa de candidatura: €532,70',
    'goldenVisaPage.documents.costs.renewal': 'Taxa de renovação: €2.663,30',
    'goldenVisaPage.documents.costs.legal': 'Honorários legais: €15.000 - €25.000',
    'goldenVisaPage.documents.costs.additional': 'Custos adicionais (traduções, apostilas): €2.000 - €5.000',
    'goldenVisaPage.faq.tag': 'FAQ',
    'goldenVisaPage.faq.title': 'Perguntas Frequentes',
    'goldenVisaPage.faq.subtitle': 'Respostas às dúvidas mais comuns sobre o Visto Gold',
    'goldenVisaPage.warnings.tag': 'IMPORTANTE',
    'goldenVisaPage.warnings.title': 'Avisos e Considerações',
    'goldenVisaPage.warnings.subtitle': 'Informações importantes antes de iniciar o processo',
    'goldenVisaPage.success.tag': 'TESTEMUNHOS',
    'goldenVisaPage.success.title': 'Histórias de Sucesso',
    'goldenVisaPage.success.subtitle': 'Conheça famílias que obtiveram com sucesso o Visto Gold',
    'goldenVisaPage.portugal.tag': 'DESTINO',
    'goldenVisaPage.portugal.title': 'Porquê Portugal?',
    'goldenVisaPage.portugal.subtitle': 'Descubra as vantagens de escolher Portugal como seu novo lar',
    'goldenVisaPage.cta.tag': 'PRÓXIMOS PASSOS',
    'goldenVisaPage.cta.title': 'Pronto para Começar?',
    'goldenVisaPage.cta.subtitle': 'Entre em contacto connosco para uma avaliação gratuita da sua elegibilidade',
    'goldenVisaPage.cta.primary': 'Agendar Consulta Gratuita',
    'goldenVisaPage.cta.secondary': 'Descarregar Checklist',
    'goldenVisaPage.form.title': 'Solicite uma Consulta Gratuita',
    'goldenVisaPage.form.subtitle': 'Os nossos especialistas irão avaliar o seu caso e apresentar as melhores opções',
    'goldenVisaPage.stickyButton': 'Consulta Gratuita',

    // Contact Page
    'contact.title': 'Contacte-nos',
    'contact.subtitle': 'Consulta premium de relocalização internacional — diagnóstico do seu projecto. Portugal em profundidade hoje; mais destinos UE em expansão.',
    'contact.heroSubtitle': 'Consulta premium de relocalização internacional — diagnóstico do seu projecto. Portugal em profundidade hoje; mais destinos UE em expansão.',
    'contact.partnersTitle': 'Receba propostas personalizadas dos nossos parceiros certificados',
    'contact.partnersDescription': 'Submeta o formulário de diagnóstico abaixo. Encaminhamos o seu perfil a parceiros licenciados no destino e área seleccionados—com Portugal como mercado flagship.',
    'contact.partnersBullets': '✓ Análise gratuita ✓ Sem compromisso ✓ Propostas directas de especialistas',
    'contact.seoTitle': 'Contacte DENVORA Mobility | Consulta de Residência Internacional',
    'contact.seoDescription': 'Formulário de diagnóstico de relocalização internacional — Portugal, Espanha, Itália e Grécia. Vistos, imóvel e encaminhamento a parceiros licenciados.',
    'contact.seoKeywords': 'contacto denvora mobility, consulta relocalização internacional, residência internacional, vistos portugal, golden visa consulta',
    'contact.inPersonByAppointment': 'Atendimento presencial apenas por marcação',
    'contact.fiscalAddressNote': 'A morada fiscal é usada apenas para fins administrativos.',
    'contact.formLoading': 'A carregar formulário…',
    'contact.formErrorFallback': `Não foi possível carregar o formulário. Actualize a página ou escreva para ${CONTACT_EMAILS.info}`,
    'contact.email': 'Email',
    'contact.phone': 'Telefone',
    'contact.office': 'Escritório',
    'contact.generalTab': 'Contacto Geral',
    'contact.appointmentTab': 'Agendar Reunião',
    'contact.generalTitle': 'Envie-nos uma Mensagem',
    'contact.generalDescription': 'Tem dúvidas sobre investir em Portugal? Estamos aqui para ajudar.',
    'contact.appointmentTitle': 'Agende uma Reunião',
    'contact.appointmentDescription': 'Escolha o melhor dia e hora para falarmos sobre os seus projetos.',
    'contact.form.name': 'Nome',
    'contact.form.email': 'Email',
    'contact.form.phone': 'Telefone',
    'contact.form.company': 'Empresa',
    'contact.form.service': 'Área de Interesse',
    'contact.form.message': 'Mensagem',
    'contact.form.date': 'Data',
    'contact.form.time': 'Horário',
    'contact.form.notes': 'Observações',
    'contact.form.required': '*',
    'contact.form.selectService': 'Selecione uma área',
    'contact.form.selectDate': 'Selecione uma data',
    'contact.form.selectTime': 'Selecione um horário',
    'contact.form.messagePlaceholder': 'Descreva como podemos ajudar...',
    'contact.form.notesPlaceholder': 'Temas que gostaria de abordar na reunião...',
    'contact.form.sendMessage': 'Enviar Mensagem',
    'contact.form.scheduleAppointment': 'Agendar Reunião',
    'contact.form.sending': 'Enviando...',
    'contact.form.scheduling': 'Agendando...',
    'contact.services.realEstate': 'Imobiliário',
    'contact.services.goldenVisa': 'Golden Visa',
    'contact.services.d7Visa': 'Visto D7',
    'contact.services.investment': 'Investimento',
    'contact.services.legal': 'Consultoria Jurídica',
    'contact.services.financing': 'Financiamento',
    'contact.services.other': 'Outros',
    'contact.success.messageSent': 'Mensagem enviada!',
    'contact.success.messageDescription': 'Entraremos em contato em breve.',
    'contact.success.appointmentScheduled': 'Reunião agendada!',
    'contact.success.appointmentDescription': 'Reunião marcada para {{date}} às {{time}}',
    'contact.error.title': 'Erro',
    'contact.error.messageDescription': 'Erro ao enviar mensagem. Tente novamente.',
    'contact.error.appointmentDescription': 'Erro ao agendar reunião. Tente novamente.',
    
    // Contact CTA for guides
    'contact.cta.title': 'Precisa de Ajuda Personalizada?',
    'contact.cta.description': 'Fale com nossos especialistas certificados e acelere sua mudança para Portugal. Consultoria gratuita e sem compromisso.',
    'contact.cta.expert_support': 'Suporte Especializado',
    'contact.cta.quick_response': 'Resposta Rápida',
    'contact.cta.free_consultation': 'Consulta Gratuita',
    'contact.cta.button': 'Falar com Especialista',
    'contact.cta.guarantee': '✓ Resposta garantida em 24h • ✓ Consulta gratuita • ✓ Especialistas certificados',
  },
  en: {
    // Navigation
    'nav.home': 'Home',
    'nav.about': 'About Us',
    'nav.blog': 'Blog',
    'nav.realEstate': 'Real Estate',
    'nav.buyProperty': 'Buy Property',
    'nav.business': 'Business',
    'nav.expatGuides': 'Expat Guides',
    'nav.interactive': 'Relocation Toolkit',
    'nav.pathways': 'Pathways',
    'nav.destinations': 'Destinations',
    'nav.portugalVisas': 'Portugal Visas',
    'nav.portugalServices': 'Portugal Services',
    'nav.toolkit': 'Toolkit',
    'nav.goldenVisa': 'Golden Visa',
    'nav.goldenVisaLink': '/golden-visa',
    'nav.partners': 'Partners',
    'nav.contact': 'Contact',
    'partners.destinations.other': 'Other markets',
    'partners.form.destinationsTitle': 'In which destinations does your company operate?',
    'partners.form.destinationsHint': 'Select all markets where you provide licensed services',
    'partners.form.otherMarkets': 'Specify other markets',
    'partners.form.coveragePortugal': 'Coverage in Portugal',
    'partners.form.coverageSpain': 'Coverage in Spain',
    'partners.form.coverageItaly': 'Coverage in Italy',
    'partners.form.coverageGreece': 'Coverage in Greece',
    'healthcare': 'Healthcare',
    
    // Common
    'common.readMore': 'Read more',
    'common.learnMore': 'Learn more',
    'common.getStarted': 'Get started',
    'common.contact': 'Contact',
    'common.minutes': 'minutes',
    'common.readTime': 'min read',
    
    // Hero page
    'hero.title': 'Your Intelligent Pathway to International Residency',
    'hero.subtitle': 'Portugal, Spain, Italy, and Greece — visas, property, and guided tools. Portugal as flagship market; Europe as your horizon.',
    'hero.cta': 'Explore Destinations',
    'hero.ctaSecondary': 'Toolkit',
    
    // Expat Guides
    'guides.title': 'Expat Guides',
    'guides.intro': 'Moving to Portugal can be a unique experience, but it involves many details to consider. In this section, we\'ve created the Expat Guides to help you understand how to settle in, manage your finances, find a place to live, and much more. Browse through our sections and start your journey informed.',
    'guides.documentation.title': 'Required Documentation',
    'guides.documentation.description': 'Learn everything you need to obtain your NIF, residence visa, and other essential documents for living in Portugal.',
    'guides.finances.title': 'Finance and Banking',
    'guides.finances.description': 'Understand how to organize your finances, open a bank account in Portugal, and how the tax system works.',
    'guides.housing.title': 'Housing and Rental',
    'guides.housing.description': 'Find the perfect property in Portugal! Learn how to rent or buy your home in the country.',
    'guides.health.title': 'Health and Health Insurance',
    'guides.health.description': 'Discover how Portugal\'s public health system works and private health insurance options.',
    'guides.investment.title': 'Investing in Portugal',
    'guides.investment.description': 'Learn more about investment opportunities in Portugal, including real estate, funds, and the Golden Visa.',
    'guides.contact.description': 'Get the best tips on how to live and invest in Portugal directly in your email.',
    
    // Interactive Features
    'quiz.title': 'What\'s the Best Guide for You?',
    'quiz.subtitle': 'Answer a few questions and discover the most relevant content for your profile',
    'quiz.progress': 'Question',
    'quiz.of': 'of',
    'quiz.completed': 'Quiz Completed!',
    'quiz.recommendation': 'Based on your answers, we recommend:',
    'quiz.startJourney': 'Start Journey',
    'quiz.restart': 'Restart Quiz',
    'quiz.changeDestination': 'Change destination',
    'quiz.loading': 'Loading pathways…',
    'quiz.noRulesTitle': 'Pathways coming soon for this destination',
    'quiz.noRulesDescription': 'We have not published visa rules for this country yet. Register your interest and our team will contact you when available.',
    'quiz.contactCta': 'Talk to an expert',
    'quiz.questions.status': 'What is your current situation?',
    'quiz.questions.interest': 'What is your main interest in Portugal?',
    'quiz.questions.interestDestination': 'What is your main interest in {destination}?',
    'quiz.questions.region': 'Which region of Portugal interests you most?',
    'quiz.options.retired': 'Retired',
    'quiz.options.working': 'Working',
    'quiz.options.investor': 'Investor',
    'quiz.options.nomad': 'Digital Nomad',
    'quiz.options.realEstate': 'Real Estate Investment',
    'quiz.options.lifestyle': 'Lifestyle',
    'quiz.options.business': 'Business',
    'quiz.options.retirementLife': 'Retirement',
    'quiz.options.lisbon': 'Lisbon',
    'quiz.options.porto': 'Porto',
    'quiz.options.algarve': 'Algarve',
    'quiz.options.anywhere': 'Anywhere',
    'quiz.results.retirement.title': 'Retirement Guide',
    'quiz.results.retirement.description': 'Perfect for those seeking quality of life and tranquility in Portugal',
    'quiz.results.investment.title': 'Investment Guide',
    'quiz.results.investment.description': 'Ideal for investors interested in the Portuguese real estate market',
    'quiz.results.nomad.title': 'Digital Nomad Guide',
    'quiz.results.nomad.description': 'Special for remote professionals who want to live in Portugal',
    'quiz.results.business.title': 'Business Guide',
    'quiz.results.business.description': 'For entrepreneurs who want to establish businesses in Portugal',

    'calculator.title': 'Cost of Living Calculator',
    'calculator.subtitle': 'Find out how much it costs to live in Portugal according to your profile',
    'calculator.cities.lisbon': 'Lisbon',
    'calculator.cities.porto': 'Porto',
    'calculator.cities.algarve': 'Algarve',
    'calculator.cities.braga': 'Braga',
    'calculator.cities.coimbra': 'Coimbra',
    'calculator.categories.housing': 'Housing',
    'calculator.categories.food': 'Food',
    'calculator.categories.transport': 'Transport',
    'calculator.categories.healthcare': 'Healthcare',
    'calculator.categories.entertainment': 'Entertainment',
    'calculator.categories.utilities': 'Utilities',
    'calculator.categories.education': 'Education',
    'calculator.categories.other': 'Other',
    'calculator.form.title': 'Your Details',
    'calculator.form.subtitle': 'Fill in the information to calculate',
    'calculator.form.income': 'Monthly Income (€)',
    'calculator.form.city': 'City of Interest',
    'calculator.form.selectCity': 'Select a city',
    'calculator.form.familySize': 'Family Size',
    'calculator.form.selectFamily': 'Select number',
    'calculator.form.housingType': 'Housing Type',
    'calculator.form.selectHousing': 'Select type',
    'calculator.form.lifestyle': 'Lifestyle',
    'calculator.form.selectLifestyle': 'Select your style',
    'calculator.form.hasCar': 'Owns a Car',
    'calculator.form.selectCar': 'Select an option',
    'calculator.form.hasChildren': 'Has School-Age Children',
    'calculator.form.selectChildren': 'Select an option',
    'calculator.form.person': 'person',
    'calculator.form.people': 'people',
    'calculator.form.calculate': 'Calculate',
    'calculator.form.reset': 'Reset',
    'calculator.housing.apartment1br': '1-Bedroom Apartment',
    'calculator.housing.apartment2br': '2-Bedroom Apartment',
    'calculator.housing.apartment3br': '3+ Bedroom Apartment',
    'calculator.housing.house': 'House/Villa',
    'calculator.housing.luxury': 'Luxury Property',
    'calculator.lifestyle.basic': 'Basic',
    'calculator.lifestyle.moderate': 'Moderate',
    'calculator.lifestyle.comfortable': 'Comfortable',
    'calculator.lifestyle.luxury': 'Luxury',
    'calculator.results.title': 'Results',
    'calculator.results.subtitle': 'Estimated monthly cost in',
    'calculator.results.empty': 'Fill out the form to see results',
    'calculator.results.total': 'Monthly Total',
    'calculator.results.percentage': 'Percentage of Income',
    'calculator.results.ofIncome': 'of your income',
    'calculator.results.fillForm': 'Fill out the form on the left to calculate',
    'calculator.assumptions.title': 'Calculation Assumptions',
    'calculator.assumptions.housing': 'Housing based on average rental prices in the selected region',
    'calculator.assumptions.food': 'Food considers supermarket shopping and some dining out',
    'calculator.assumptions.transport': 'Transport includes monthly passes or fuel and maintenance costs',
    'calculator.assumptions.healthcare': 'Healthcare based on basic private insurance and occasional consultations',
    'calculator.assumptions.entertainment': 'Entertainment includes cultural activities, restaurants and leisure',
    'calculator.assumptions.utilities': 'Utilities include water, electricity, gas, internet and mobile phone',
    'calculator.assumptions.education': 'Education considers private school costs or extracurricular activities',
    'calculator.assumptions.other': 'Other expenses include clothing, personal care and unexpected costs',
    'calculator.assumptions.note': 'Note: These values are estimates based on 2024 data and may vary according to individual needs.',

    'interactiveGuides.title': 'Interactive Guides by Profile',
    'interactiveGuides.subtitle': 'Profile-based guides — Portugal fully built; other destinations expanding',
    'interactiveGuides.learnMore': 'Learn More',
    'interactiveGuides.retirement.title': 'Retirees',
    'interactiveGuides.retirement.description': 'Guide for a peaceful retirement in Europe — with Portugal as flagship market',
    'interactiveGuides.retirement.feature1': 'Quality healthcare system',
    'interactiveGuides.retirement.feature2': 'Affordable cost of living',
    'interactiveGuides.retirement.feature3': 'Mild climate year round',
    'interactiveGuides.nomad.title': 'Digital Nomads',
    'interactiveGuides.nomad.description': 'Work remotely in Europe — visas and lifestyle, with in-depth Portugal guides',
    'interactiveGuides.nomad.feature1': 'High-speed internet',
    'interactiveGuides.nomad.feature2': 'Coworking spaces',
    'interactiveGuides.nomad.feature3': 'International community',
    'interactiveGuides.investor.title': 'Investors',
    'interactiveGuides.investor.description': 'Investment and residency opportunities — Golden Visa and property in Portugal; pathways in other destinations',
    'interactiveGuides.investor.feature1': 'Golden Visa available',
    'interactiveGuides.investor.feature2': 'Stable real estate market',
    'interactiveGuides.investor.feature3': 'Return on investment',
    'interactiveGuides.business.title': 'Entrepreneurs',
    'interactiveGuides.business.description': 'Establish a business in Europe — Portugal as flagship; partners across PT/ES/IT/GR',
    'interactiveGuides.business.feature1': 'Easy company setup',
    'interactiveGuides.business.feature2': 'Tax incentives',
    'interactiveGuides.business.feature3': 'Access to European market',
    'interactiveGuides.family.title': 'Families',
    'interactiveGuides.family.description': 'Family relocation in Europe — reunification and residency, with detailed Portugal guides',
    'interactiveGuides.family.feature1': 'Quality public education',
    'interactiveGuides.family.feature2': 'Safety and tranquility',
    'interactiveGuides.family.feature3': 'Activities for the whole family',

    'interactive.page.title': 'Relocation Toolkit',
    'interactive.page.subtitle': 'Data-guided tools to map your visa pathway, living costs, and ideal region—before you speak with an advisor.',

    'toolkit.eyebrow': 'International Residency Platform',
    'toolkit.title': 'Relocation Toolkit',
    'toolkit.subtitle': 'Live tools to map your profile, costs, and eligibility—Portugal in full depth today, with more EU destinations on the roadmap.',
    'toolkit.cta.hub': 'View destinations & pathways',
    'toolkit.cta.all': 'Open all tools',
    'toolkit.items.quiz.title': 'Pathway quiz',
    'toolkit.items.quiz.description': 'Answer in 2 minutes to find the guide and visa route that fits your profile.',
    'toolkit.items.quiz.cta': 'Start quiz',
    'toolkit.items.calculator.title': 'Cost of living calculator',
    'toolkit.items.calculator.description': 'Estimate monthly spend by city and household size.',
    'toolkit.items.calculator.cta': 'Calculate costs',
    'toolkit.items.map.title': 'Regional map',
    'toolkit.items.map.description': 'Compare regions by profile: retiree, nomad, investor, or family.',
    'toolkit.items.map.cta': 'Explore map',
    'toolkit.items.eligibility.title': 'Golden Visa eligibility',
    'toolkit.items.eligibility.description': 'Check initial requirements before a private consultation.',
    'toolkit.items.eligibility.cta': 'Check eligibility',
    'toolkit.items.advisor.title': 'Advisor intake',
    'toolkit.items.advisor.description': 'Guided qualification and introductions to licensed partners.',
    'toolkit.items.advisor.cta': 'Talk to an expert',
    'toolkit.related.allVisas': 'View all Portugal visas',
    'toolkit.related.pathwaysHub': 'Compare international destinations',
    'toolkit.related.d7': 'D7 visa guide (passive income)',
    'toolkit.related.d8': 'D8 visa guide (digital nomad)',
    'toolkit.related.goldenVisa': 'Golden Visa Portugal guide',
    'toolkit.related.startup': 'Startup Visa Portugal guide',

    'destinations.title': 'International destinations',
    'portugalSpotlight.badge': 'Flagship market',
    'portugalSpotlight.title': 'Portugal Spotlight',
    'portugalSpotlight.subtitle': 'Our fully built market with detailed visa guides and a licensed partner network.',
    'portugalSpotlight.golden.title': 'Golden Visa',
    'portugalSpotlight.golden.description': 'Qualified investment residency with EU access — funds, capital, and alternative routes.',
    'portugalSpotlight.golden.cta': 'Golden Visa guide',
    'portugalSpotlight.d6.title': 'D6 Visa — Family Reunification',
    'portugalSpotlight.d6.description': 'Bring spouse, children, and dependent parents to join family already resident in Portugal.',
    'portugalSpotlight.d6.cta': 'D6 visa guide',
    'portugalSpotlight.hubCta': 'Portugal hub',
    'portugalSpotlight.allVisasCta': 'All Portugal visas',
    'portugalSpotlight.consultCta': 'Free consultation',
    'destinations.subtitle': 'Portugal is our flagship market with full content and partners. The platform architecture is built for EU expansion.',
    'destinations.status.active': 'Available',
    'destinations.status.overview': 'Pathway overview',
    'destinations.status.comingSoon': 'Coming soon',
    'destinations.explorePathways': 'Explore pathways',
    'destinations.registerInterest': 'Register interest',
    'destinations.portugal.name': 'Portugal',
    'destinations.portugal.tagline': 'Golden Visa, D7, D8, property, and healthcare—your EU gateway.',
    'destinations.spain.name': 'Spain',
    'destinations.spain.tagline': 'PT vs ES comparator, four residency routes, and interest registration with concierge.',
    'destinations.italy.name': 'Italy',
    'destinations.italy.tagline': 'Visas and lifestyle—in development.',
    'destinations.greece.name': 'Greece',
    'destinations.greece.tagline': 'Golden Visa and coast—in development.',

    'chatInvite.title': 'Need help?',
    'chatInvite.body': 'Need help with international residency in the EU? Talk to our DENVORA Mobility assistant — choose your destination in the chat.',
    'chatInvite.cta': 'Start conversation',

    'diagnostic.step1.title': 'What is your relocation destination?',
    'diagnostic.step1.subtitle': 'Portugal has full pathways today. You can select other EU destinations to register your interest.',
    'diagnostic.step2.title': 'In which areas do you need specialized support?',
    'diagnostic.step2.subtitlePortugal': 'Full pathways available for Portugal.',
    'diagnostic.step2.subtitleOther': 'Interest registered for {destination} — our team will contact you when the destination goes live.',
    'diagnostic.step2.comingSoonNote': 'The detailed questions below are calibrated for Portugal. Your interest in the selected destination will still be recorded and shared with our team.',

    'pathways.page.title': 'Destinations & Residency Pathways',
    'pathways.page.subtitle': 'Compare markets, use pathway tools, and move forward with discreet concierge. Portugal has full guides; Spain has a comparator and route overview; Italy and Greece are open for interest registration.',
    'pathways.page.cta.advisor': 'Private consultation',
    'pathways.pillars.title': 'Portugal pathways — visa guides',
    'pathways.pillars.subtitle': 'Go deeper on each residency route after using the toolkit. Full bilingual guides, kept current.',
    'pathways.pillars.cta': 'Read guide',
    'pathways.pillars.goldenVisa.title': 'Golden Visa',
    'pathways.pillars.goldenVisa.description': 'Qualified investment residency — 2026 requirements and eligible routes.',
    'pathways.pillars.d7.title': 'D7 Visa',
    'pathways.pillars.d7.description': 'For retirees and passive income — minimum income and documentation.',
    'pathways.pillars.d8.title': 'D8 Visa',
    'pathways.pillars.d8.description': 'Digital nomads and remote work for employers outside Portugal.',
    'pathways.pillars.startup.title': 'Startup Visa',
    'pathways.pillars.startup.description': 'Entrepreneurs with an innovative project and growth potential in Portugal.',
    'pathways.pillars.allVisas.title': 'All D visas',
    'pathways.pillars.allVisas.description': 'Full D1–D8 hub with comparison and links to each dedicated page.',
    'pathways.comparator.title': 'Compare pathways: Portugal vs Spain',
    'pathways.comparator.subtitle': 'Same profile, two markets — minimum income, timelines, and residency route side by side. Indicative data from the Pathway Engine.',
    'pathways.comparator.loading': 'Loading comparator…',
    'pathways.comparator.family.passiveIncome': 'Passive income',
    'pathways.comparator.family.digitalNomad': 'Digital nomad',
    'pathways.comparator.family.investor': 'Investor',
    'pathways.comparator.family.entrepreneur': 'Entrepreneur',
    'pathways.comparator.minIncome': 'Min. income',
    'pathways.comparator.processing': 'Processing',
    'pathways.comparator.citizenship': 'Citizenship (years)',
    'pathways.comparator.months': 'months',
    'pathways.comparator.years': 'years',
    'pathways.comparator.viewGuide': 'View guide',
    'pathways.comparator.registerInterest': 'Register interest',
    'pathways.comparator.noRuleYet': 'Rules publishing soon for this destination — register your interest.',
    'pathways.comparator.disclaimer': 'Indicative information only. Official requirements change—confirm with immigration counsel before deciding.',
    'pathways.spain.badge': 'Pathway overview',
    'pathways.spain.title': 'Spain residency pathways',
    'pathways.spain.subtitle': 'Four main routes for passive income, remote work, investment, and entrepreneurship—aligned with the Portugal vs Spain comparator.',
    'pathways.spain.intro': 'We do not yet have page-by-page visa guides like Portugal. This section summarises indicative requirements from the Pathway Engine and routes you to the quiz or a private consultation for Spain.',
    'pathways.spain.highlight1': 'Schengen access and strong relocation demand across the Iberian market',
    'pathways.spain.highlight2': 'Routes for retirement, remote work, qualified capital, and business projects',
    'pathways.spain.highlight3': 'Compare side by side with Portugal in the comparator above',
    'pathways.spain.cardCta': 'Register interest',
    'pathways.spain.ctaQuiz': 'Quiz with Spain destination',
    'pathways.spain.ctaContact': 'Private consultation — Spain',
    'pathways.spain.exploreCta': 'View Spain pathways',
    'pathways.spain.loading': 'Loading Spain pathways…',
    'pathways.spain.disclaimer': 'Indicative values (minimum income, timelines, citizenship). Spanish law evolves—legal validation required before applying.',
    'pathways.italy.badge': 'Pathway overview',
    'pathways.italy.title': 'Italy residency pathways',
    'pathways.italy.subtitle': 'Four routes for passive income, remote work, qualified investment, and innovative projects.',
    'pathways.italy.intro': 'We do not yet have page-by-page visa guides like Portugal. This section summarises indicative requirements from the Pathway Engine and routes you to the quiz or a private consultation for Italy.',
    'pathways.italy.highlight1': 'Schengen access, Mediterranean lifestyle, and strong digital-nomad hubs',
    'pathways.italy.highlight2': 'Elective residence, digital nomad visa, investor route (from €250k), and startup visa',
    'pathways.italy.highlight3': 'Register interest while we expand dedicated Italy content',
    'pathways.italy.cardCta': 'Register interest',
    'pathways.italy.ctaQuiz': 'Quiz with Italy destination',
    'pathways.italy.ctaContact': 'Private consultation — Italy',
    'pathways.italy.exploreCta': 'View Italy pathways',
    'pathways.italy.loading': 'Loading Italy pathways…',
    'pathways.italy.disclaimer': 'Indicative values (minimum income, timelines, citizenship). Italian law evolves—legal validation required before applying.',
    'pathways.greece.badge': 'Pathway overview',
    'pathways.greece.title': 'Greece residency pathways',
    'pathways.greece.subtitle': 'Financially independent permit, digital nomad visa, real-estate Golden Visa, and business residence.',
    'pathways.greece.intro': 'We do not yet have page-by-page visa guides like Portugal. This section summarises indicative requirements from the Pathway Engine and routes you to the quiz or a private consultation for Greece.',
    'pathways.greece.highlight1': 'Schengen access, competitive cost of living, and a well-known real-estate Golden Visa',
    'pathways.greece.highlight2': 'Routes for retirement, remote work, property investment, and business projects',
    'pathways.greece.highlight3': 'Use the quiz with Greece selected to match profile and pathway',
    'pathways.greece.cardCta': 'Register interest',
    'pathways.greece.ctaQuiz': 'Quiz with Greece destination',
    'pathways.greece.ctaContact': 'Private consultation — Greece',
    'pathways.greece.exploreCta': 'View Greece pathways',
    'pathways.greece.loading': 'Loading Greece pathways…',
    'pathways.greece.disclaimer': 'Indicative values (minimum income, timelines, citizenship). Greek law evolves—legal validation required before applying.',
    'pathways.faq.title': 'Frequently asked questions — international residency',
    'pathways.faq.q1': 'What is the international residency platform?',
    'pathways.faq.a1': 'It is DENVORA Mobility\'s guided area combining tools (quiz, calculator, map), visa content, and human support. It helps you choose country and residency route before speaking with an advisor.',
    'pathways.faq.q2': 'Which destinations are live today?',
    'pathways.faq.a2': 'Portugal is the flagship market with a full toolkit and dedicated visa guides. Spain, Italy, and Greece have pathway overviews, quiz, and interest registration—Spain includes a PT vs ES comparator. Page-by-page guides outside Portugal are in development.',
    'pathways.faq.q3': 'How do the toolkit tools work together?',
    'pathways.faq.a3': 'The pathway quiz works for Portugal, Spain, Italy, and Greece. The calculator and map focus on Portugal; the comparator covers Portugal vs Spain. Advisor intake qualifies your project and attaches your selected destination.',
    'pathways.faq.q4': 'Does this platform replace legal advice?',
    'pathways.faq.a4': 'No. Tools and guides are informational and orientational. Visa, tax, and investment decisions must be validated with immigration counsel and licensed advisors.',
    'pathways.faq.q5': 'How do I request a private consultation?',
    'pathways.faq.a5': 'Use the Private consultation button or toolkit advisor intake. Your selected destination (Portugal by default) is attached to the request for correct routing.',

    'interactiveMap.title': 'Regional Opportunities Map',
    'interactiveMap.description': 'Discover the best regions of Portugal for your profile and goals',
    'interactiveMap.legend.retirees': 'Retirees',
    'interactiveMap.legend.nomads': 'Digital Nomads',
    'interactiveMap.legend.investors': 'Investors',
    'interactiveMap.costOfLiving': 'Cost of Living',
    'interactiveMap.highlights': 'Highlights',
    'interactiveMap.clickInstructions': 'Click on markers to see details of each region',

    // Footer
    'footer.description': 'International residency intelligence across Portugal, Spain, Italy, and Greece. Visas, pathways, property, and discreet concierge.',
    'footer.destinations': 'Destinations',
    'footer.company': 'Company',
    'footer.newsletter.title': 'Get exclusive tips in your email',
    'footer.newsletter.description': 'Weekly newsletter with opportunities, market analysis and practical guides',
    'footer.newsletter.placeholder': 'Your email',
    'footer.newsletter.subscribe': 'Subscribe',
    'footer.quickLinks': 'Quick Links',
    'footer.services': 'Services',
    'footer.services.buyerAgent': 'Buyer Agent',
    'footer.services.goldenVisa': 'Golden Visa',
    'footer.services.d7Visa': 'D7 Visa',
    'footer.services.investment': 'Investment',
    'footer.services.legalConsultancy': 'Legal Consultancy',
    'footer.services.financing': 'Financing',
    'footer.legal': 'Legal',
    'footer.legal.privacy': 'Privacy Policy',
    'footer.legal.terms': 'Terms of Use',
    'footer.legal.gdpr': 'GDPR',
    'footer.legal.complaints': 'Complaints Book',
    'footer.followUs': 'Follow Us',
    'footer.rights': 'All rights reserved.',

    // Golden Visa Section
    'goldenVisa.badge': 'GOLDEN VISA',
    'goldenVisa.title': 'Portugal Golden Visa',
    'goldenVisa.subtitle': 'Secure residency in Portugal and free movement in Europe through strategic investment. The fastest path to European citizenship.',
    'goldenVisa.stats.countries': 'EU Countries',
    'goldenVisa.stats.years': 'Years to citizenship (7 for CPLP/EU)',
    'goldenVisa.stats.days': 'Days/2 years minimum',
    'goldenVisa.stats.investment': 'Minimum investment',
    'goldenVisa.benefitsTitle': 'Key Benefits',
    'goldenVisa.benefits.euAccess.title': 'EU Access',
    'goldenVisa.benefits.euAccess.description': 'Free movement across 27 European Union countries',
    'goldenVisa.benefits.family.title': 'Family Inclusion',
    'goldenVisa.benefits.family.description': 'Spouse, children and parents included in the process',
    'goldenVisa.benefits.minimal.title': 'Minimal Stay',
    'goldenVisa.benefits.minimal.description': 'Only 14 days every 2 years to maintain residency',
    'goldenVisa.benefits.investment.title': 'Multiple Options',
    'goldenVisa.benefits.investment.description': 'Various investment modalities available',
    'goldenVisa.optionsTitle': 'Investment Routes',
    'goldenVisa.options.funds.title': 'Investment Funds',
    'goldenVisa.options.funds.description': 'Investment in licensed venture capital funds',
    'goldenVisa.options.funds.details': 'Qualified funds focused on innovation, technology, or supporting Portuguese SMEs',
    'goldenVisa.options.business.title': 'Job Creation',
    'goldenVisa.options.business.amount': 'Variable',
    'goldenVisa.options.business.description': 'Investment with minimum creation of 5 jobs',
    'goldenVisa.options.business.details': 'Creation of at least 5 permanent jobs or 4 in low-density areas',
    'goldenVisa.options.culture.title': 'Arts & Culture',
    'goldenVisa.options.culture.description': 'Investment in cultural heritage and artistic projects',
    'goldenVisa.options.culture.details': 'Cultural heritage recovery, support for national artistic production, or urban reconstruction',
    'goldenVisa.options.research.title': 'Scientific Research',
    'goldenVisa.options.research.description': 'Investment in research activities developed by institutions',
    'goldenVisa.options.research.details': 'Public or private institutions integrated in the national scientific and technological system',
    'goldenVisa.options.capital.title': 'Capital Transfer',
    'goldenVisa.options.capital.description': 'Capital transfer of an amount equal to or greater than 1,500,000 euros',
    'goldenVisa.options.capital.details': 'To a bank account in Portugal or investment in Portuguese financial instruments',
    'goldenVisa.options.support.title': 'National Production Support',
    'goldenVisa.options.support.description': 'Investment in the incorporation of a commercial company headquartered in Portugal',
    'goldenVisa.options.support.details': 'Creation of 5 jobs or capital increase to strengthen business activity',
    'goldenVisa.popular': 'Most Popular',
    'goldenVisa.cta.title': 'Ready for the Next Step?',
    'goldenVisa.cta.description': 'Discover if you are eligible for the Golden Visa and start your journey to European residency with guidance from our specialists.',
    'goldenVisa.cta.learn': 'Learn more about Golden Visa',
    'goldenVisa.cta.consult': 'Free Consultation',

    // CTA Section
    'cta.title': 'Connect with Trusted Experts',
    'cta.subtitle': 'Access the best services through our network of specialized partners. Each one is carefully selected for their expertise and commitment to excellence.',
    'cta.services.real_estate.title': 'Real Estate',
    'cta.services.real_estate.description': 'Find the perfect home with Homesconsult - Buyer&apos;s Agent specialized in working for you',
    'cta.services.real_estate.cta': 'Contact Homesconsult',
    'cta.services.finance.title': 'Finance',
    'cta.services.finance.description': 'Optimize your investments and financing with our trusted financial partners',
    'cta.services.finance.cta': 'Learn more',
    'cta.services.business.title': 'Business',
    'cta.services.business.description': 'Open or expand your business in Europe with partner support — Portugal, Spain, Italy, and Greece',
    'cta.services.business.cta': 'Learn more',
    'cta.partner_highlight': 'Main Partner',
    'cta.partners_verified': 'All our partners are carefully verified and recommended based on their experience and proven results.',
    'cta.certified_experts': 'Certified Experts',
    'cta.portuguese_support': 'Portuguese Support',
    'cta.proven_experience': 'Proven Experience',

    // Educational Content
    'educational.title': 'Educational Content',
    'educational.subtitle': 'Insights on residency and property across our destinations — Portugal featured; Spain, Italy, and Greece expanding',
    'educational.read_more': 'Read more',
    'educational.view_all': 'View All Articles',

    // Services Section
    'services.title': 'Trusted Partners',
    'services.subtitle': 'We connect you with the best specialists in each area, carefully selected for their expertise and commitment to excellence',
    'services.realEstate.title': 'Real Estate & Buyer Agent',
    'services.realEstate.description': 'Property buying support with buyer agent — Portugal flagship (Homesconsult); other destinations via partners',
    'services.realEstate.details': 'We connect you with Homesconsult and buyer agents in Portugal. For Spain, Italy, and Greece, we route to licensed partners in each market.',
    'services.realEstate.cta': 'Contact Homesconsult',
    'services.financial.title': 'Financial Services & Golden Visa',
    'services.financial.description': 'Network of partners for financing, Golden Visa and tax optimization',
    'services.financial.details': 'Golden Visa and financing consultants covering Portugal, Spain, Italy, and Greece — deepest coverage in Portugal.',
    'services.financial.cta': 'Learn more',
    'services.business.title': 'Business Support',
    'services.business.description': 'Partners for company formation and management across PT, ES, IT, and GR markets',
    'services.business.details': 'From company setup to accounting and legal advice — we connect you with professionals in the destinations you choose.',
    'services.business.cta': 'Learn more',

    // Blog Section
    'blog.title': 'Blog',
    'blog.subtitle': 'Articles on residency, visas, and opportunities — Portugal featured; multi-destination perspective growing',
    'blog.featured': 'Featured',
    'blog.readMore': 'Read full article',
    'blog.viewAll': 'View All Blog Articles',
    'blog.filter.all': 'All',
    'blog.filter.europe': 'Europe & Pathways',
    'blog.filter.portugal': 'Portugal',
    'blog.filter.spain': 'Spain',
    'blog.filter.italy': 'Italy',
    'blog.filter.greece': 'Greece',
    'blog.filter.topic.all': 'All topics',
    'blog.filter.topic.pathways': 'Pathways',
    'blog.filter.topic.visas': 'Visas & residency',
    'blog.filter.topic.property': 'Real estate',
    'blog.filter.topic.living': 'Living',
    'blog.filter.topic.tax': 'Tax',
    'blog.filter.count': '{n} articles',
    'blog.empty.title': 'Content coming soon',
    'blog.empty.spain': 'We are preparing articles on residency in Spain. Explore Pathways or register your interest with us.',
    'blog.empty.italy': 'We are preparing articles on residency in Italy. Explore Pathways or register your interest with us.',
    'blog.empty.greece': 'We are preparing articles on residency in Greece. Explore Pathways or register your interest with us.',
    'blog.empty.ctaPathways': 'View Pathways',
    'blog.empty.ctaContact': 'Request consultation',
    'blog.hub.ctaDestination': 'Explore destination hub',
    'blog.hub.noTopicMatch': 'No articles in this topic — try another filter or browse all articles for this destination.',
    'blog.articles.residenceLicense.category': 'Residence',
    'blog.articles.residenceLicense.title': 'Residence License in Portugal: The Ultimate Guide to Living Your Dream',
    'blog.articles.residenceLicense.excerpt': 'The decision to move to Portugal is the first step of an incredible journey. Understand the legal process to secure your residence.',
    'blog.articles.lisbonMarket.category': 'Real Estate Market',
    'blog.articles.lisbonMarket.title': 'Lisbon: The Real Estate Market of the Moment',
    'blog.articles.lisbonMarket.excerpt': 'Lisbon is not just a historic capital; it is also one of the hottest real estate markets in Europe.',
    'blog.articles.buyingProperty.category': 'Property Purchase',
    'blog.articles.buyingProperty.title': 'The Essential Guide: The Property Buying Process in Portugal',
    'blog.articles.buyingProperty.excerpt': 'The decision to buy a house in Portugal is an exciting step. This guide shows each step of the journey clearly.',
    'blog.articles.investmentPortugal.category': 'Investment',
    'blog.articles.investmentPortugal.title': 'Investing in Portugal: Strategies and Opportunities for a Solid Portfolio',
    'blog.articles.investmentPortugal.excerpt': 'Portugal has emerged as a beacon for international real estate investors. Discover strategies to maximize returns.',

    // Calculator Toast Messages
    'calculator.toast.required_fields.title': 'Required fields',
    'calculator.toast.required_fields.description': 'Please fill in all fields before calculating.',
    'calculator.toast.invalid_value.title': 'Invalid value',
    'calculator.toast.invalid_value.description': 'Please enter a valid income value.',
    'calculator.toast.calculation_done.title': 'Calculation completed!',
    'calculator.toast.form_cleared.title': 'Form cleared',
    'calculator.toast.form_cleared.description': 'All fields have been reset.',

    // Golden Visa Page Specific Content
    'goldenVisaPage.title': 'Golden Visa Portugal - Residency through Investment',
    'goldenVisaPage.description': 'Obtain the Portuguese Golden Visa through strategic investment. Residency in Portugal, EU access and path to European citizenship.',
    'goldenVisaPage.hero.badge': 'OFFICIAL PROGRAM',
    'goldenVisaPage.hero.title': 'Portugal Golden Visa',
    'goldenVisaPage.hero.subtitle': 'Obtain Portuguese residency and free access to the European Union through qualified investment. The most direct path to European citizenship.',
    'goldenVisaPage.hero.cta.primary': 'Check Eligibility',
    'goldenVisaPage.hero.cta.secondary': 'Download Complete Guide',
    'goldenVisaPage.overview.title': 'Program Overview',
    'goldenVisaPage.overview.description': 'The Portuguese Golden Visa is a residency by investment program that offers third-country nationals the opportunity to obtain residency authorization in Portugal through qualified investment.',
    'goldenVisaPage.overview.features.title': 'Key Features',
    'goldenVisaPage.overview.features.1': 'Residency authorization valid for 2 years, renewable for 3-year periods',
    'goldenVisaPage.overview.features.2': 'Possibility to apply for Portuguese citizenship after 10 years of legal residence, or 7 years for CPLP or EU citizens. Applications submitted by 18 May 2026 could still use the previous 5-year period.',
    'goldenVisaPage.overview.features.3': 'Free movement in the Schengen Area',
    'goldenVisaPage.overview.features.4': 'Inclusion of spouse, minor children and dependent parents',
    'goldenVisaPage.benefits.tag': 'BENEFITS',
    'goldenVisaPage.benefits.title': 'Golden Visa Benefits',
    'goldenVisaPage.benefits.subtitle': 'Discover all the advantages the program offers for you and your family',
    'goldenVisaPage.routes.tag': 'INVESTMENT ROUTES',
    'goldenVisaPage.routes.title': 'Available Investment Routes',
    'goldenVisaPage.routes.subtitle': 'Choose the investment modality that best suits your profile',
    'goldenVisaPage.eligibility.tag': 'REQUIREMENTS',
    'goldenVisaPage.eligibility.title': 'Eligibility Criteria',
    'goldenVisaPage.eligibility.subtitle': 'Check if you meet the requirements to apply for the Golden Visa',
    'goldenVisaPage.eligibility.requirements.title': 'General Requirements',
    'goldenVisaPage.eligibility.requirements.1': 'Be a third-country national (outside EU/EEA/Switzerland)',
    'goldenVisaPage.eligibility.requirements.2': 'Have no criminal record',
    'goldenVisaPage.eligibility.requirements.3': 'Have means of subsistence',
    'goldenVisaPage.eligibility.requirements.4': 'Have health insurance',
    'goldenVisaPage.eligibility.requirements.5': 'Comply with one of the investment modalities',
    'goldenVisaPage.presence.tag': 'PRESENCE',
    'goldenVisaPage.presence.title': 'Physical Presence Requirements',
    'goldenVisaPage.presence.subtitle': 'Minimum time required to stay to maintain residency',
    'goldenVisaPage.presence.minimum.title': 'Minimum Presence',
    'goldenVisaPage.presence.minimum.description': 'To maintain the Golden Visa, you must stay in Portugal:',
    'goldenVisaPage.presence.minimum.first': '7 days, consecutive or interpolated, in the first year',
    'goldenVisaPage.presence.minimum.subsequent': '14 days, consecutive or interpolated, in each subsequent 2-year period',
    'goldenVisaPage.presence.minimum.total': 'Overall: 35 days across the first 5 years (7 + 14 + 14)',
    'goldenVisaPage.process.tag': 'PROCESS',
    'goldenVisaPage.process.title': 'Process Timeline',
    'goldenVisaPage.process.subtitle': 'Understand each step of the application process',
    'goldenVisaPage.documents.tag': 'DOCUMENTATION',
    'goldenVisaPage.documents.title': 'Documents and Costs',
    'goldenVisaPage.documents.subtitle': 'Complete list of required documents and associated costs',
    'goldenVisaPage.documents.required.title': 'Required Documents',
    'goldenVisaPage.documents.costs.title': 'Process Costs',
    'goldenVisaPage.documents.costs.application': 'Application fee: €532.70',
    'goldenVisaPage.documents.costs.renewal': 'Renewal fee: €2,663.30',
    'goldenVisaPage.documents.costs.legal': 'Legal fees: €15,000 - €25,000',
    'goldenVisaPage.documents.costs.additional': 'Additional costs (translations, apostilles): €2,000 - €5,000',
    'goldenVisaPage.faq.tag': 'FAQ',
    'goldenVisaPage.faq.title': 'Frequently Asked Questions',
    'goldenVisaPage.faq.subtitle': 'Answers to the most common questions about the Golden Visa',
    'goldenVisaPage.warnings.tag': 'IMPORTANT',
    'goldenVisaPage.warnings.title': 'Warnings and Considerations',
    'goldenVisaPage.warnings.subtitle': 'Important information before starting the process',
    'goldenVisaPage.success.tag': 'TESTIMONIALS',
    'goldenVisaPage.success.title': 'Success Stories',
    'goldenVisaPage.success.subtitle': 'Meet families who successfully obtained the Golden Visa',
    'goldenVisaPage.portugal.tag': 'DESTINATION',
    'goldenVisaPage.portugal.title': 'Why Portugal?',
    'goldenVisaPage.portugal.subtitle': 'Discover the advantages of choosing Portugal as your new home',
    'goldenVisaPage.cta.tag': 'NEXT STEPS',
    'goldenVisaPage.cta.title': 'Ready to Start?',
    'goldenVisaPage.cta.subtitle': 'Contact us for a free assessment of your eligibility',
    'goldenVisaPage.cta.primary': 'Schedule Free Consultation',
    'goldenVisaPage.cta.secondary': 'Download Checklist',
    'goldenVisaPage.form.title': 'Request a Free Consultation',
    'goldenVisaPage.form.subtitle': 'Our specialists will evaluate your case and present the best options',
    'goldenVisaPage.stickyButton': 'Free Consultation',

    // Contact Page
    'contact.title': 'Contact Us',
    'contact.subtitle': 'Premium international relocation consultation—diagnostic intake for your project. Portugal in full depth today; more EU destinations expanding.',
    'contact.heroSubtitle': 'Premium international relocation consultation—diagnostic intake for your project. Portugal in full depth today; more EU destinations expanding.',
    'contact.partnersTitle': 'Get personalized proposals from our certified partners',
    'contact.partnersDescription': 'Submit the diagnostic form below. We route your profile to licensed partners for your destination and focus area—with Portugal as the flagship market today.',
    'contact.partnersBullets': '✓ Free analysis ✓ No commitment ✓ Direct proposals from specialists',
    'contact.seoTitle': 'Contact DENVORA Mobility | International Residency Consultation',
    'contact.seoDescription': 'International relocation diagnostic form — Portugal, Spain, Italy, and Greece. Visas, property, and introductions to licensed partners.',
    'contact.seoKeywords': 'contact denvora mobility, international relocation consultation, international residency, portugal visas, golden visa consultation',
    'contact.inPersonByAppointment': 'In-person service by appointment only',
    'contact.fiscalAddressNote': 'The fiscal address is used for administrative purposes only.',
    'contact.formLoading': 'Loading form…',
    'contact.formErrorFallback': `The form could not load. Please refresh or email us at ${CONTACT_EMAILS.info}`,
    'contact.email': 'Email',
    'contact.phone': 'Phone',
    'contact.office': 'Office',
    'contact.generalTab': 'General Contact',
    'contact.appointmentTab': 'Schedule Meeting',
    'contact.generalTitle': 'Send us a Message',
    'contact.generalDescription': 'Have questions about investing in Portugal? We are here to help.',
    'contact.appointmentTitle': 'Schedule a Meeting',
    'contact.appointmentDescription': 'Choose the best day and time to discuss your projects.',
    'contact.form.name': 'Name',
    'contact.form.email': 'Email',
    'contact.form.phone': 'Phone',
    'contact.form.company': 'Company',
    'contact.form.service': 'Area of Interest',
    'contact.form.message': 'Message',
    'contact.form.date': 'Date',
    'contact.form.time': 'Time',
    'contact.form.notes': 'Notes',
    'contact.form.required': '*',
    'contact.form.selectService': 'Select an area',
    'contact.form.selectDate': 'Select a date',
    'contact.form.selectTime': 'Select a time',
    'contact.form.messagePlaceholder': 'Describe how we can help...',
    'contact.form.notesPlaceholder': 'Topics you would like to discuss in the meeting...',
    'contact.form.sendMessage': 'Send Message',
    'contact.form.scheduleAppointment': 'Schedule Meeting',
    'contact.form.sending': 'Sending...',
    'contact.form.scheduling': 'Scheduling...',
    'contact.services.realEstate': 'Real Estate',
    'contact.services.goldenVisa': 'Golden Visa',
    'contact.services.d7Visa': 'D7 Visa',
    'contact.services.investment': 'Investment',
    'contact.services.legal': 'Legal Consultancy',
    'contact.services.financing': 'Financing',
    'contact.services.other': 'Other',
    'contact.success.messageSent': 'Message sent!',
    'contact.success.messageDescription': 'We will contact you shortly.',
    'contact.success.appointmentScheduled': 'Meeting scheduled!',
    'contact.success.appointmentDescription': 'Meeting scheduled for {{date}} at {{time}}',
    'contact.error.title': 'Error',
    'contact.error.messageDescription': 'Error sending message. Please try again.',
    'contact.error.appointmentDescription': 'Error scheduling meeting. Please try again.',
    
    // Contact CTA for guides
    'contact.cta.title': 'Need Personalized Help?',
    'contact.cta.description': 'Speak with our certified specialists and accelerate your move to Portugal. Free consultation with no commitment.',
    'contact.cta.expert_support': 'Expert Support',
    'contact.cta.quick_response': 'Quick Response',
    'contact.cta.free_consultation': 'Free Consultation',
    'contact.cta.button': 'Speak with Specialist',
    'contact.cta.guarantee': '✓ Guaranteed response within 24h • ✓ Free consultation • ✓ Certified specialists',
  }
};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Set initial language based on domain IMMEDIATELY to prevent flash
  const getInitialLanguage = (): Language => {
    if (typeof window !== 'undefined') {
      const domainLanguage = getLanguageFromHostname(window.location.hostname);
      if (domainLanguage) return domainLanguage;
      const saved = localStorage.getItem('language') as Language;
      return saved || 'pt';
    }
    return 'pt';
  };

  const [language, setLanguage] = useState<Language>(getInitialLanguage);

  useEffect(() => {
    const hostname = window.location.hostname;
    const domainLanguage = getLanguageFromHostname(hostname);
    
    if (domainLanguage) {
      if (language !== domainLanguage) {
        setLanguage(domainLanguage);
      }
      localStorage.setItem('language', domainLanguage);
    } else {
      const savedLanguage = localStorage.getItem('language') as Language;
      if (savedLanguage && savedLanguage !== language) {
        setLanguage(savedLanguage);
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('language', language);
    
    // Update document language
    document.documentElement.lang = language === 'pt' ? 'pt-PT' : 'en-GB';
    
    document.title = getBrandTitle(language);
  }, [language]);

  const t = (key: string): string => {
    return translations[language][key as keyof typeof translations['pt']] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};